import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  Tag,
  X,
  Check,
  ChevronsUpDown,
  Eye,
  UploadCloud,
  Loader2,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Building2,
  User,
  Stethoscope,
  DollarSign,
  AlertCircle,
  FileText,
  Search,
  Sparkles,
  Info,
  ShieldCheck,
  ArrowLeft,
  Layers,
  GitBranch,
  CreditCard,
  RefreshCw,
  Star
} from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  useListBundles,
  useCreateBundle,
  useUpdateBundle,
  getListBundlesQueryKey,
  useListCptCodes,
  useListMasterCptCodes,
  useGetProvider,
  useGetLocalities,
  useListBundleCategories,
  useGetCptCategories,
  useGetCptSubcategories,
  API_BASE
} from "@/lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useRoute } from "wouter";
import { navigatePostSaveBundle } from "@/lib/utils";

const STEPS = ["Location & Scope", "Bundle Details", "Billing Codes", "Review & Pricing"];

export default function CreateBundlePage() {
  const [, setLocation] = useLocation();
  const [matchEdit, paramsEdit] = useRoute<{ id: string }>("/provider/bundles/edit/:id");
  const editId = matchEdit && paramsEdit ? paramsEdit.id : null;

  const [step, setStep] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // User State
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch (e) { }
    }
  }, []);

  const isGroup = localStorage.getItem("practiceType") === "group";

  // Step 1 State: Location & Scope
  const [selectedLocation, setSelectedLocation] = useState<string>("default");
  const [bundleScope, setBundleScope] = useState<"GROUP" | "PROVIDER">("GROUP");
  const [participatingProviderIds, setParticipatingProviderIds] = useState<string[]>([]);

  // Step 2 State: Bundle Info & Media
  const [form, setForm] = useState({
    name: "",
    description: "",
    category: "Medical",
    categoryType: "Office Visits",
    discountPercent: "0",
    finalPrice: "",
    status: "active"
  });

  // Tag Cloud State
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [existingTags, setExistingTags] = useState<string[]>([]);

  // Subscription / Payment Plan State
  const [isSubscription, setIsSubscription] = useState<boolean>(false);
  const [subscriptionInterval, setSubscriptionInterval] = useState<string>("MONTHLY");
  const [recurringAmount, setRecurringAmount] = useState<string>("");
  const [billingCycles, setBillingCycles] = useState<string>("");
  const [isFeatured, setIsFeatured] = useState<boolean>(false);

  // Thumbnail Image State
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Step 3 State: Billing Codes & Items
  const [selectedCpts, setSelectedCpts] = useState<string[]>([]);
  const [cptUnits, setCptUnits] = useState<Record<string, number>>({});
  const [cptAdjustments, setCptAdjustments] = useState<
    Record<string, { type: "percent" | "fixed"; value: number; percent?: number; fixed?: number; adjustedPrice?: number }>
  >({});
  const [cptSearch, setCptSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Custom Items State
  const [customItems, setCustomItems] = useState<
    { id: string; name: string; description?: string; price: number; unit?: number }[]
  >([]);
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customUnit, setCustomUnit] = useState("1");

  // Step 4 State: Review & Disclaimer
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Queries
  const { data: localitiesData = [] } = useGetLocalities();
  const { data: currentProviderData } = useGetProvider(user?.id || "", { query: { enabled: !!user } });
  const myLinkedProviders = currentProviderData?.linkedProviders || [];
  // Any provider with accepted linked providers should also get full scope/participating provider management
  const hasLinkedProviders = isGroup || (myLinkedProviders.length > 0);
  const { data: providerCptCodesData } = useListCptCodes({ limit: 5000 });
  const providerCptCodes = providerCptCodesData?.items || [];
  const { data: rawMasterCodesData } = useListMasterCptCodes({ limit: 5000 });
  const rawMasterCodes = rawMasterCodesData?.items || [];
  const { data: rawBundleCategoriesData = [] } = useListBundleCategories();
  const { data: cptCategoriesData = [] } = useGetCptCategories();
  const { data: cptSubcategoriesData = [] } = useGetCptSubcategories();
  const { data: bundlesData } = useListBundles({ limit: 1000 });

  const createBundle = useCreateBundle();
  const updateBundle = useUpdateBundle();

  const effectiveUser = currentProviderData || user;

  const normalizeStateCode = (st: string) => {
    if (!st) return "";
    const s = st.trim().toUpperCase();
    if (s === "NEW YORK" || s === "NEW YORK STATE" || s === "N.Y." || s === "NY") return "NY";
    if (s === "CALIFORNIA" || s === "CA") return "CA";
    if (s === "TEXAS" || s === "TX") return "TX";
    if (s === "FLORIDA" || s === "FL") return "FL";
    return s;
  };

  const providerLocalities = useMemo(() => {
    const selectedLocalityKeys = new Set<string>();

    const addrList = effectiveUser?.addresses || effectiveUser?.user?.addresses;
    if (addrList && Array.isArray(addrList)) {
      addrList.forEach((a: any) => {
        const ids = a.localityIds || (a.localityId ? [a.localityId] : []);
        if (Array.isArray(ids)) {
          ids.forEach((id: any) => {
            if (id) selectedLocalityKeys.add(String(id).trim());
          });
        }
      });
    }

    const locList = effectiveUser?.localities || effectiveUser?.provider?.localities || effectiveUser?.user?.localities;
    if (selectedLocalityKeys.size === 0 && locList && Array.isArray(locList)) {
      locList.forEach((l: any) => {
        if (typeof l === "string") {
          selectedLocalityKeys.add(l.trim());
        } else if (l && typeof l === "object") {
          if (l.id) selectedLocalityKeys.add(String(l.id).trim());
          if (l.localityNumber) selectedLocalityKeys.add(String(l.localityNumber).trim());
          if (l.name) selectedLocalityKeys.add(String(l.name).trim());
        }
      });
    }

    const locMap = new Map<string, any>();
    const targetKeys = Array.from(selectedLocalityKeys);

    if (Array.isArray(localitiesData) && localitiesData.length > 0) {
      if (targetKeys.length > 0) {
        localitiesData.forEach((loc: any) => {
          const locIdStr = String(loc.id).trim();
          const locNumStr = String(loc.localityNumber || '').trim();
          const locNameLower = String(loc.name || '').trim().toLowerCase();

          for (const key of targetKeys) {
            if (!key) continue;
            const keyLower = key.toLowerCase();
            if (locIdStr === key || (locNumStr && locNumStr === key) || locNameLower === keyLower) {
              locMap.set(locIdStr, loc);
              break;
            }
          }
        });
      }

      if (locMap.size === 0) {
        const stateKeys = new Set<string>();
        if (addrList && Array.isArray(addrList)) {
          addrList.forEach((a: any) => {
            if (a.state) {
              const norm = normalizeStateCode(a.state);
              if (norm) stateKeys.add(norm);
            }
          });
        }
        if (stateKeys.size === 0) stateKeys.add("NY");

        localitiesData.forEach((loc: any) => {
          if (loc.state && stateKeys.has(normalizeStateCode(loc.state))) {
            locMap.set(String(loc.id).trim(), loc);
          }
        });
      }
    }

    return Array.from(locMap.values());
  }, [effectiveUser, localitiesData]);

  const providerLocations = useMemo(() => {
    const addrList = effectiveUser?.addresses || effectiveUser?.user?.addresses || [];
    const locations: Array<{
      id: string;
      displayLabel: string;
      locality: any;
      localityId?: string;
    }> = [];

    if (Array.isArray(addrList) && addrList.length > 0) {
      addrList.forEach((addr: any, index: number) => {
        const street = [addr.buildingNo, addr.street1, addr.street2].filter(Boolean).join(" ");
        const cityStateZip = [addr.city, addr.state, addr.zipCode].filter(Boolean).join(", ");
        const fullAddress = [street, cityStateZip].filter(Boolean).join(" - ") || `Location ${index + 1}`;
        const isPrimaryTag = addr.isPrimary ? " (Primary)" : "";
        const displayLabel = `${fullAddress}${isPrimaryTag}`;

        let targetLocality: any = null;
        const locIds = addr.localityIds || (addr.localityId ? [addr.localityId] : []);

        if (Array.isArray(localitiesData) && localitiesData.length > 0) {
          if (locIds.length > 0) {
            targetLocality = localitiesData.find((l: any) =>
              locIds.some((id: any) => String(l.id) === String(id) || String(l.localityNumber) === String(id))
            );
          }
          if (!targetLocality && addr.state) {
            const normState = normalizeStateCode(addr.state);
            targetLocality = localitiesData.find((l: any) => normalizeStateCode(l.state) === normState);
          }
        }

        locations.push({
          id: addr.id || `location-${index}`,
          displayLabel,
          locality: targetLocality || providerLocalities[0] || null,
          localityId: targetLocality?.id || providerLocalities[0]?.id
        });
      });
    }

    if (locations.length === 0 && providerLocalities.length > 0) {
      providerLocalities.forEach((loc: any) => {
        locations.push({
          id: String(loc.id),
          displayLabel: `${loc.name}${loc.state ? `, ${loc.state}` : ''}`,
          locality: loc,
          localityId: String(loc.id)
        });
      });
    }

    return locations;
  }, [effectiveUser, localitiesData, providerLocalities]);

  useEffect(() => {
    if (providerLocations.length > 0) {
      const isAlreadySelectedValid = providerLocations.some(l => String(l.id) === String(selectedLocation));
      if (!selectedLocation || selectedLocation === "default" || !isAlreadySelectedValid) {
        setSelectedLocation(String(providerLocations[0].id));
      }
    }
  }, [providerLocations, selectedLocation]);

  // Load existing tags
  useEffect(() => {
    const fetchExistingTags = async () => {
      try {
        let token = localStorage.getItem("token") || "";
        if (!token) {
          try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            if (u.token) token = u.token;
          } catch (err) { }
        }
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(`${API_BASE}/bundles/tags`, { headers });
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setExistingTags(json.data);
        }
      } catch (err) {
        console.error("Failed to fetch bundle tags:", err);
      }
    };
    fetchExistingTags();
  }, []);

  const parseBundleTags = (bundle: any): string[] => {
    if (!bundle) return [];
    const raw = bundle.tags || bundle.searchTags || bundle.filterTags || bundle.keywords;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map((t: any) => String(t).trim().toLowerCase().replace(/^#/, '')).filter(Boolean);
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((t: any) => String(t).trim().toLowerCase().replace(/^#/, '')).filter(Boolean);
        }
      } catch (e) {}
      return raw
        .split(/[,;\s]+/)
        .map((t: string) => t.trim().toLowerCase().replace(/^#/, ''))
        .filter(Boolean);
    }
    return [];
  };

  // Load bundle data if editing
  useEffect(() => {
    if (!editId) return;

    const applyBundleData = (bundle: any) => {
      if (!bundle) return;
      setForm({
        name: bundle.name || "",
        description: bundle.description || "",
        category: bundle.category || "Medical",
        categoryType: (bundle as any).categoryType || "Office Visits",
        discountPercent: bundle.discountPercent !== undefined ? bundle.discountPercent.toString() : "0",
        finalPrice: bundle.finalPrice !== undefined ? bundle.finalPrice.toString() : "",
        status: bundle.status || "active"
      });
      setSelectedCpts(bundle.cptCodes?.map((c: any) => c.id) || []);
      const rawUnits = (bundle as any).cptUnits || {};
      setCptUnits(rawUnits);
      const loadedAdjustments = (bundle as any).cptAdjustments || rawUnits._adjustments || {};
      setCptAdjustments(loadedAdjustments);
      setCustomItems(Array.isArray((bundle as any).customItems) ? (bundle as any).customItems : []);
      setThumbnailUrl(bundle.thumbnailUrl || null);
      setSelectedTags(parseBundleTags(bundle));

      setIsSubscription(Boolean((bundle as any).isSubscription));
      setSubscriptionInterval((bundle as any).subscriptionInterval || "MONTHLY");
      setRecurringAmount((bundle as any).recurringAmount !== undefined && (bundle as any).recurringAmount !== null ? String((bundle as any).recurringAmount) : "");
      setBillingCycles((bundle as any).billingCycles !== undefined && (bundle as any).billingCycles !== null ? String((bundle as any).billingCycles) : "");
      setIsFeatured(Boolean((bundle as any).isFeatured));

      if ((bundle as any).locationId || (bundle as any).selectedLocation) {
        setSelectedLocation(String((bundle as any).locationId || (bundle as any).selectedLocation));
      }

      const isProviderSpecific = bundle.type?.startsWith("PROVIDER_");
      const pProviders = (bundle as any).participatingProviders;

      if (pProviders && Array.isArray(pProviders.providerIds) && pProviders.providerIds.length > 0) {
        setParticipatingProviderIds(pProviders.providerIds.map(String));
        setBundleScope(pProviders.isAll ? "GROUP" : "PROVIDER");
      } else if (isProviderSpecific) {
        const pId = bundle.type!.split("_")[1];
        const matchedLp = myLinkedProviders.find(
          (lp: any) => String(lp.providerId) === String(pId) || String(lp.id) === String(pId)
        );
        setParticipatingProviderIds(matchedLp ? [String(matchedLp.providerId || matchedLp.id)] : [pId]);
        setBundleScope("PROVIDER");
      } else {
        setBundleScope("GROUP");
      }
    };

    let foundInList = false;
    if (bundlesData?.items) {
      const bundle = bundlesData.items.find((b: any) => String(b.id) === String(editId));
      if (bundle) {
        applyBundleData(bundle);
        foundInList = true;
      }
    }

    if (!foundInList) {
      const fetchSingleBundle = async () => {
        try {
          let token = localStorage.getItem("token") || "";
          if (!token) {
            try {
              const u = JSON.parse(localStorage.getItem("user") || "{}");
              if (u.token) token = u.token;
            } catch (err) {}
          }
          const headers: Record<string, string> = {};
          if (token) headers["Authorization"] = `Bearer ${token}`;

          const res = await fetch(`${API_BASE}/bundles/public/${editId}`, { headers });
          const json = await res.json();
          if (json.success && json.data) {
            applyBundleData(json.data);
          }
        } catch (err) {
          console.error("Failed to fetch single bundle for edit:", err);
        }
      };
      fetchSingleBundle();
    }
  }, [editId, bundlesData, myLinkedProviders]);

  // Handle Thumbnail Upload
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("thumbnail", file);
      let token = localStorage.getItem("token") || "";
      if (!token) {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "{}");
          if (u.token) token = u.token;
        } catch (err) { }
      }
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/upload/thumbnail`, {
        method: "POST",
        headers,
        body: formData
      });
      const json = await res.json();
      if (json.url) {
        setThumbnailUrl(json.url);
      } else {
        throw new Error(json.error || "Upload failed");
      }
    } catch (err: any) {
      toast({ title: "Upload Failed", description: err.message || "Failed to upload thumbnail", variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const [thumbError, setThumbError] = useState(false);
  useEffect(() => {
    setThumbError(false);
  }, [thumbnailUrl]);

  const getImageUrl = (url?: string | null) => {
    if (!url || url === "null" || url === "undefined") return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("blob:")) return url;
    const baseUrl = API_BASE.replace(/\/v1.*$/, "");
    const cleanUrl = url.startsWith("/") ? url : `/${url}`;
    return `${baseUrl}${cleanUrl}`;
  };

  // CPT Codes List - Only display selected CPT codes from provider fee schedule
  const allAvailableCptCodes = useMemo(() => {
    const masterMap = new Map<string, any>();
    (rawMasterCodes || []).forEach((m: any) => masterMap.set(m.code, m));

    if (Array.isArray(providerCptCodes) && providerCptCodes.length > 0) {
      return providerCptCodes.map((item: any) => {
        const master = masterMap.get(item.code) || item;
        const price = item.price !== undefined && item.price !== null ? item.price : (master.medicareRate || master.standardPrice || master.price || 100);
        return {
          ...master,
          ...item,
          id: item.id || master?.id || item.code,
          originalMasterId: master?.id || item.id,
          providerOverrideId: item.id,
          code: item.code,
          description: item.description || master?.description || master?.codeShortDesc || item.code,
          category: item.category || master?.category || "General",
          subCategory: item.subCategory || item.subcategory || master?.subCategory || master?.subcategory || "General",
          price,
          isProviderCode: true
        };
      });
    }

    return (rawMasterCodes || []).map((m: any) => ({
      ...m,
      id: m.id || m.code,
      code: m.code,
      description: m.description || m.codeShortDesc || m.code,
      category: m.category || "General",
      subCategory: m.subCategory || m.subcategory || "General",
      price: m.medicareRate || m.price || 100,
      isMasterCode: true
    }));
  }, [providerCptCodes, rawMasterCodes]);

  // Actual CPT Code Category Options (from DB master data & codes)
  // Dynamic Master Bundle Categories & Sub-types from DB (MasterBundleCategory)
  const bundleMainCategoryOptions = useMemo(() => {
    const catsSet = new Set<string>();
    if (Array.isArray(rawBundleCategoriesData)) {
      rawBundleCategoriesData.forEach((item: any) => {
        if (item.categoryName && item.categoryName.trim()) {
          catsSet.add(item.categoryName.trim());
        }
      });
    }

    const defaults = [
      "Medical",
      "Health & Wellness",
      "Dental",
      "Vision",
      "Therapist",
      "Pharmacy"
    ];
    defaults.forEach(c => catsSet.add(c));

    return Array.from(catsSet);
  }, [rawBundleCategoriesData]);

  const bundleCategoryTypeOptions = useMemo(() => {
    const selectedCat = (form.category || "Medical").trim().toLowerCase();
    const typesSet = new Set<string>();

    if (Array.isArray(rawBundleCategoriesData)) {
      rawBundleCategoriesData.forEach((item: any) => {
        if (
          item.categoryName &&
          item.categoryName.trim().toLowerCase() === selectedCat &&
          item.categoryType &&
          item.categoryType.trim()
        ) {
          typesSet.add(item.categoryType.trim());
        }
      });
    }

    if (typesSet.size === 0) {
      if (selectedCat === "medical") {
        ["Office Visits", "Preventive Care & Wellness", "Chronic Care Management", "Screening/Diagnostic", "Immunization/Vaccines", "Procedures & Treatments"].forEach(t => typesSet.add(t));
      } else if (selectedCat === "health & wellness") {
        ["Mental Health", "Men's Health", "Women's Health"].forEach(t => typesSet.add(t));
      } else if (selectedCat === "dental") {
        ["Preventive", "Basic", "Major"].forEach(t => typesSet.add(t));
      } else if (selectedCat === "vision") {
        ["Eye Exam", "Contact Lens / Frames"].forEach(t => typesSet.add(t));
      } else if (selectedCat === "therapist") {
        ["PT", "OT"].forEach(t => typesSet.add(t));
      } else if (selectedCat === "pharmacy") {
        ["Tier 1 Generic", "Tier 2 Brand", "Tier 3"].forEach(t => typesSet.add(t));
      } else {
        typesSet.add("General");
      }
    }

    return Array.from(typesSet);
  }, [rawBundleCategoriesData, form.category]);

  useEffect(() => {
    if (bundleCategoryTypeOptions.length > 0) {
      setForm(prev => {
        if (!prev.categoryType || !bundleCategoryTypeOptions.includes(prev.categoryType)) {
          return { ...prev, categoryType: bundleCategoryTypeOptions[0] };
        }
        return prev;
      });
    }
  }, [bundleCategoryTypeOptions]);

  const masterCptCategoryOptions = useMemo(() => {
    const catsSet = new Set<string>();

    if (Array.isArray(cptCategoriesData)) {
      cptCategoriesData.forEach((cat: any) => {
        const name = typeof cat === "string" ? cat : (cat?.name || cat?.categoryName || cat?.code || "");
        if (name && name.trim()) catsSet.add(name.trim());
      });
    }

    (rawMasterCodes || []).forEach((c: any) => {
      const cat = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (cat && cat.trim()) catsSet.add(cat.trim());
    });

    (allAvailableCptCodes || []).forEach((c: any) => {
      const cat = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (cat && cat.trim()) catsSet.add(cat.trim());
    });

    if (catsSet.size === 0) {
      [
        "Anesthesia", "Dentistry", "Diagnostic Cardiology", "Evaluation & Management",
        "General", "Medicine", "Pathology", "Physical Therapy", "Radiology", "Surgery"
      ].forEach(c => catsSet.add(c));
    }

    const categoriesList = Array.from(catsSet).sort();
    return ["All Categories", ...categoriesList];
  }, [cptCategoriesData, rawMasterCodes, allAvailableCptCodes]);

  // Actual CPT Code Subcategory Options (from DB master data & codes)
  const masterCptSubcategoryOptions = useMemo(() => {
    const selectedCat = form.category || "";
    const subsSet = new Set<string>();

    if (Array.isArray(cptSubcategoriesData)) {
      cptSubcategoriesData.forEach((s: any) => {
        const parentCat = typeof s.parentCategory === "object"
          ? s.parentCategory?.name
          : (s.parentCategory || s.categoryName || s.category?.name || "");
        if (selectedCat && selectedCat !== "All Categories" && selectedCat !== "all" && parentCat && parentCat.toLowerCase() !== selectedCat.toLowerCase()) {
          return;
        }
        const subName = typeof s === "string" ? s : (s?.name || s?.subCategoryName || s?.code || "");
        if (subName && subName.trim()) subsSet.add(subName.trim());
      });
    }

    const checkAndAddCodeSub = (c: any) => {
      const cat = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");
      if (selectedCat && selectedCat !== "All Categories" && selectedCat !== "all" && cat && cat.toLowerCase() !== selectedCat.toLowerCase()) {
        return;
      }
      const sub = typeof c.subCategory === "object" && c.subCategory !== null
        ? (c.subCategory.name || c.subCategory.code || "")
        : String(c.subCategory || c.subcategory || "");
      if (sub && sub.trim()) subsSet.add(sub.trim());
    };

    (rawMasterCodes || []).forEach(checkAndAddCodeSub);
    (allAvailableCptCodes || []).forEach(checkAndAddCodeSub);

    const subList = Array.from(subsSet).sort();
    return ["All Subcategories", ...subList];
  }, [cptSubcategoriesData, rawMasterCodes, allAvailableCptCodes, form.category]);

  // Auto-sync form.category & form.categoryType with CPT code categories
  useEffect(() => {
    if (masterCptCategoryOptions.length > 0) {
      setForm(prev => {
        if (!prev.category || !masterCptCategoryOptions.includes(prev.category)) {
          return { ...prev, category: masterCptCategoryOptions[0] };
        }
        return prev;
      });
    }
  }, [masterCptCategoryOptions]);

  useEffect(() => {
    if (masterCptSubcategoryOptions.length > 0) {
      setForm(prev => {
        if (!prev.categoryType || !masterCptSubcategoryOptions.includes(prev.categoryType)) {
          return { ...prev, categoryType: masterCptSubcategoryOptions[0] };
        }
        return prev;
      });
    }
  }, [masterCptSubcategoryOptions]);

  const filteredCptCodes = useMemo(() => {
    return allAvailableCptCodes.filter((c: any) => {
      const q = cptSearch.toLowerCase().trim();
      const codeMatch = !q || (c.code || "").toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q);

      const catName = typeof c.category === "object" && c.category !== null
        ? (c.category.name || c.category.code || "")
        : String(c.category || "");

      const subName = typeof c.subCategory === "object" && c.subCategory !== null
        ? (c.subCategory.name || c.subCategory.code || "")
        : String(c.subCategory || c.subcategory || "");

      const catMatch = !form.category || form.category === "All Categories" || form.category === "all" || catName.toLowerCase() === form.category.toLowerCase();
      const subMatch = !form.categoryType || form.categoryType === "All Subcategories" || form.categoryType === "all" || subName.toLowerCase() === form.categoryType.toLowerCase();

      return codeMatch && (catName ? catMatch : true) && (subName ? subMatch : true);
    });
  }, [allAvailableCptCodes, cptSearch, form.category, form.categoryType]);

  // Pricing calculation
  const getAdjustedCptPrice = (cptCodeObj: any, id: string) => {
    const basePrice = Number(cptCodeObj?.price || 0);
    const adj = cptAdjustments[id];
    if (!adj) return basePrice;

    if (adj.adjustedPrice !== undefined && !isNaN(adj.adjustedPrice)) {
      return adj.adjustedPrice;
    }
    if (adj.fixed !== undefined && adj.fixed > 0) {
      return Math.max(0, Math.round((basePrice - adj.fixed) * 100) / 100);
    }
    if (adj.percent !== undefined && adj.percent > 0) {
      return Math.max(0, Math.round((basePrice * (1 - Math.min(100, adj.percent) / 100)) * 100) / 100);
    }
    if (!adj.value || adj.value <= 0) return basePrice;

    if (adj.type === "percent") {
      const discounted = basePrice * (1 - Math.min(100, Math.max(0, adj.value)) / 100);
      return Math.max(0, Math.round(discounted * 100) / 100);
    } else if (adj.type === "fixed") {
      const discounted = basePrice - adj.value;
      return Math.max(0, Math.round(discounted * 100) / 100);
    }

    return basePrice;
  };

  const calculatedTotal = useMemo(() => {
    const cptTotal = selectedCpts.reduce((sum, id) => {
      const code = allAvailableCptCodes.find((c: any) => c.id === id || c.originalMasterId === id || c.providerOverrideId === id);
      const unit = cptUnits[id] || 1;
      const adjustedPrice = getAdjustedCptPrice(code, id);
      return sum + adjustedPrice * unit;
    }, 0);
    const customTotal = customItems.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.unit) || 1), 0);
    return cptTotal + customTotal;
  }, [selectedCpts, allAvailableCptCodes, cptUnits, cptAdjustments, customItems]);

  const prevCalcTotalRef = useRef(0);

  useEffect(() => {
    if (calculatedTotal !== prevCalcTotalRef.current) {
      const isInitialEditLoad = editId && prevCalcTotalRef.current === 0 && calculatedTotal > 0;

      if (!isInitialEditLoad) {
        setForm(prev => {
          const discount = Number(prev.discountPercent || 0);
          return {
            ...prev,
            finalPrice: calculatedTotal ? (calculatedTotal * (1 - discount / 100)).toFixed(2) : ""
          };
        });
      }

      prevCalcTotalRef.current = calculatedTotal;
    }
  }, [calculatedTotal, editId]);

  const finalPriceValue = useMemo(() => {
    if (form.finalPrice !== "") {
      return Number(form.finalPrice) || 0;
    }
    const discount = Number(form.discountPercent) || 0;
    return calculatedTotal * (1 - discount / 100);
  }, [form.finalPrice, form.discountPercent, calculatedTotal]);

  const toggleCpt = (id: string) => {
    setSelectedCpts(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  // Submit Handler
  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast({ title: "Name required", description: "Please enter a bundle name.", variant: "destructive" });
      setStep(1);
      return;
    }
    if (selectedCpts.length === 0 && customItems.length === 0) {
      toast({ title: "Items required", description: "Please add at least one CPT code or custom item to the bundle.", variant: "destructive" });
      setStep(2);
      return;
    }

    let finalP = calculatedTotal * (1 - Number(form.discountPercent) / 100);
    let finalD = Number(form.discountPercent);

    if (form.finalPrice) {
      finalP = Number(form.finalPrice);
      finalD = calculatedTotal ? ((calculatedTotal - finalP) / calculatedTotal) * 100 : 0;
    }

    const matchedLookup = (rawBundleCategoriesData || []).find(
      (c: any) =>
        (c.categoryName || "").toLowerCase() === (form.category || "").toLowerCase() &&
        (c.categoryType || "").toLowerCase() === (form.categoryType || "").toLowerCase()
    );

    const payload: any = {
      name: form.name,
      description: form.description,
      category: form.category,
      categoryType: form.categoryType,
      bundleCategoryId: matchedLookup?.id || undefined,
      status: form.status,
      cptCodeIds: selectedCpts,
      cptUnits: {
        ...cptUnits,
        _adjustments: cptAdjustments
      },
      cptAdjustments,
      totalPrice: calculatedTotal,
      finalPrice: finalP,
      discountPercent: parseFloat(finalD.toFixed(2)),
      customItems,
      thumbnailUrl,
      tags: selectedTags,
      locationId: selectedLocation !== "default" ? selectedLocation : undefined,
      isSubscription,
      subscriptionInterval: isSubscription ? subscriptionInterval : undefined,
      recurringAmount: isSubscription && recurringAmount ? parseFloat(recurringAmount) : undefined,
      billingCycles: isSubscription && billingCycles ? parseInt(billingCycles, 10) : undefined,
      isFeatured,
      type: hasLinkedProviders ? (bundleScope === "GROUP" ? "GROUP" : "PROVIDER") : "INDIVIDUAL",
      participatingProviderIds: hasLinkedProviders ? participatingProviderIds : undefined,
      participatingProviders: hasLinkedProviders
        ? {
            isAll:
              bundleScope === "GROUP" ||
              (myLinkedProviders.length > 0 && participatingProviderIds.length === myLinkedProviders.length),
            providerIds: participatingProviderIds
          }
        : undefined
    };

    if (editId) {
      updateBundle.mutate(
        { id: editId, data: payload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
            toast({ title: "Bundle updated", description: `${form.name} has been updated successfully.` });
            navigatePostSaveBundle(setLocation);
          },
          onError: (err: any) => {
            toast({ title: "Update Failed", description: err.message || "Could not update bundle.", variant: "destructive" });
          }
        }
      );
    } else {
      createBundle.mutate(
        { data: payload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListBundlesQueryKey() });
            toast({ title: "Bundle created", description: `${form.name} has been created successfully.` });
            navigatePostSaveBundle(setLocation);
          },
          onError: (err: any) => {
            toast({ title: "Creation Failed", description: err.message || "Could not create bundle.", variant: "destructive" });
          }
        }
      );
    }
  };

  return (
    <ProviderLayout>
      <div className="w-full p-3 sm:p-4 max-w-6xl mx-auto space-y-3">
        {/* Header Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setLocation("/provider/bundles")}
              className="rounded-lg h-8 w-8 border-border hover:bg-muted"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-md font-medium text-[11px] px-2 py-0">
                  {editId ? "Edit Mode" : "GFE Bundle"}
                </Badge>
              </div>
              <h1 className="text-xl font-bold tracking-tight mt-0.5">
                {editId ? "Edit Service Bundle" : "Create New Service Bundle"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setLocation("/provider/bundles")}
              className="rounded-lg h-8 px-3 text-xs"
            >
              Cancel
            </Button>
            {step === STEPS.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={createBundle.isPending || updateBundle.isPending || (!disclaimerAccepted && !editId)}
                className="bg-gradient-to-r from-primary to-secondary text-white rounded-lg h-8 px-3 text-xs shadow-sm gap-1.5"
              >
                {createBundle.isPending || updateBundle.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                )}
                {editId ? "Save Changes" : "Publish Service Bundle"}
              </Button>
            ) : (
              <Button
                onClick={() => setStep(prev => Math.min(STEPS.length - 1, prev + 1))}
                className="bg-primary text-white rounded-lg h-8 px-3.5 text-xs gap-1 shadow-xs"
              >
                Next Step <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Multi-Step Stepper Bar */}
        <div className="bg-card border border-border rounded-xl p-2.5 shadow-2xs">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            {STEPS.map((s, i) => (
              <button
                key={s}
                onClick={() => setStep(i)}
                className={`flex items-center gap-2 text-left w-full md:w-auto transition-all ${
                  i === step ? "opacity-100 font-semibold" : i < step ? "opacity-90" : "opacity-50"
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all shadow-2xs ${
                    i < step
                      ? "bg-primary text-white"
                      : i === step
                      ? "bg-primary text-white ring-2 ring-primary/20"
                      : "bg-muted text-muted-foreground border border-border"
                  }`}
                >
                  {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground font-medium leading-none">Step {i + 1}</div>
                  <div className="text-xs font-semibold text-foreground mt-0.5">{s}</div>
                </div>
                {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground hidden md:block ml-2" />}
              </button>
            ))}
          </div>
        </div>

        {/* Step Content Area */}
        <AnimatePresence mode="wait">
          {/* STEP 1: Location & Scope */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="space-y-3"
            >
              <div className="bg-card border border-border rounded-xl p-4 shadow-2xs space-y-3">
                <div>
                  <h2 className="text-base font-bold flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" /> Service Location & Ownership
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Select facility location and bundle scope (facility-wide or provider specific).
                  </p>
                </div>

                {/* Location Selection */}
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs font-semibold">Primary Service Location / Facility</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="rounded-lg h-9 text-xs bg-background">
                      <SelectValue placeholder="Select service facility..." />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl text-xs">
                      {providerLocations.length > 0 ? (
                        providerLocations.map((loc: any) => (
                          <SelectItem key={loc.id} value={String(loc.id)}>
                            {loc.displayLabel}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="default">
                          {user?.provider?.facilityName || user?.name || "Main Facility Center"} (Primary Location)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    All CPT billing codes and pricing overrides will be associated with this location.
                  </p>
                </div>

                {/* Scope Selection */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs font-semibold">Bundle Scope / Level</Label>
                  <RadioGroup
                    value={bundleScope}
                    onValueChange={(val: "GROUP" | "PROVIDER") => setBundleScope(val)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    <div
                      onClick={() => setBundleScope("GROUP")}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-start gap-2.5 ${
                        bundleScope === "GROUP"
                          ? "border-primary bg-primary/5 shadow-2xs"
                          : "border-border hover:border-primary/40 bg-card"
                      }`}
                    >
                      <RadioGroupItem value="GROUP" id="scope-group" className="mt-0.5" />
                      <div>
                        <label htmlFor="scope-group" className="font-semibold text-xs cursor-pointer">
                          Facility Level Bundle
                        </label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Standard package available facility-wide across all departments.
                        </p>
                      </div>
                    </div>

                    <div
                      onClick={() => setBundleScope("PROVIDER")}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-start gap-2.5 ${
                        bundleScope === "PROVIDER"
                          ? "border-primary bg-primary/5 shadow-2xs"
                          : "border-border hover:border-primary/40 bg-card"
                      }`}
                    >
                      <RadioGroupItem value="PROVIDER" id="scope-provider" className="mt-0.5" />
                      <div>
                        <label htmlFor="scope-provider" className="font-semibold text-xs cursor-pointer">
                          Provider Specific Bundle
                        </label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Custom bundle offered exclusively by designated clinicians.
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Provider Multi-Select if Provider Specific Scope */}
                {bundleScope === "PROVIDER" && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-primary" /> Select Attending Provider(s)
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {myLinkedProviders.length > 0 ? (
                        myLinkedProviders.map((prov: any) => {
                          const pId = String(prov.providerId || prov.id);
                          const isSelected = participatingProviderIds.includes(pId);
                          return (
                            <div
                              key={pId}
                              onClick={() => {
                                setParticipatingProviderIds(prev =>
                                  isSelected ? prev.filter(id => id !== pId) : [...prev, pId]
                                );
                              }}
                              className={`p-2.5 rounded-lg border cursor-pointer transition-all flex items-center gap-2.5 ${
                                isSelected
                                  ? "border-primary bg-primary/10 text-primary font-medium"
                                  : "border-border hover:bg-muted text-foreground"
                              }`}
                            >
                              <Checkbox checked={isSelected} />
                              <span className="text-xs font-medium">
                                {prov.name || `Provider ${pId}`}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="col-span-full p-2.5 rounded-lg bg-muted/40 text-xs text-muted-foreground text-center">
                          Default Logged-in Provider Context Selected
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Bundle Details */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="space-y-3"
            >
              <div className="bg-card border border-border rounded-xl p-4 shadow-2xs space-y-3">
                <div>
                  <h2 className="text-base font-bold flex items-center gap-2">
                    <Package className="w-4 h-4 text-primary" /> Basic Information & Thumbnail
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Set up bundle title, description, categories, thumbnail, and search tags.
                  </p>
                </div>

                {/* Compact Thumbnail Banner */}
                <div className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-muted/20 border border-border">
                  <div className="flex items-center gap-3">
                    {thumbnailUrl && !thumbError ? (
                      <div className="relative h-12 w-12 rounded-lg overflow-hidden border border-border group shrink-0 bg-muted/30">
                        <img
                          src={getImageUrl(thumbnailUrl)}
                          alt="Thumbnail preview"
                          className="h-full w-full object-cover"
                          onError={() => setThumbError(true)}
                        />
                        <button
                          type="button"
                          onClick={() => setThumbnailUrl(null)}
                          className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-12 w-12 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground bg-background shrink-0">
                        <Package className="h-5 w-5 opacity-40" />
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold">Bundle Thumbnail Banner</p>
                      <p className="text-[11px] text-muted-foreground">PNG, JPG, WEBP up to 5MB</p>
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background text-xs font-medium text-foreground cursor-pointer hover:bg-muted transition-colors shadow-2xs shrink-0">
                    {uploadingImage ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <UploadCloud className="h-3.5 w-3.5 text-muted-foreground" />}
                    {uploadingImage ? "Uploading..." : thumbnailUrl ? "Change Image" : "Upload Banner"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailUpload} disabled={uploadingImage} />
                  </label>
                </div>

                {/* Form Fields Grid */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold">Bundle Name *</Label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Annual Comprehensive Wellness Bundle"
                      className="mt-1 rounded-lg h-9 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold flex items-center justify-between">
                        <span>Bundle Category *</span>
                        <span className="text-[10px] text-muted-foreground font-normal">Primary Category</span>
                      </Label>
                      <Select
                        value={form.category}
                        onValueChange={val => setForm(prev => ({ ...prev, category: val }))}
                      >
                        <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background">
                          <SelectValue placeholder="Select Category" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl text-xs max-h-60">
                          {bundleMainCategoryOptions.map((cat: string) => (
                            <SelectItem key={cat} value={cat} className="text-xs py-1.5 cursor-pointer">
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold flex items-center justify-between">
                        <span>Category Type *</span>
                        <span className="text-[10px] text-muted-foreground font-normal">Sub-category Type</span>
                      </Label>
                      <Select
                        value={form.categoryType}
                        onValueChange={val => setForm(prev => ({ ...prev, categoryType: val }))}
                      >
                        <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background">
                          <SelectValue placeholder="Select Category Type" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl text-xs max-h-60">
                          {bundleCategoryTypeOptions.map((type: string) => (
                            <SelectItem key={type} value={type} className="text-xs py-1.5 cursor-pointer">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Description</Label>
                    <Input
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="Describe services included, preparation notes, patient expectations..."
                      className="mt-1 rounded-lg h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Tag Cloud Component */}
                <div className="space-y-1.5 pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-primary" /> Search & Filter Tags
                    </Label>
                    {selectedTags.length > 0 && (
                      <span className="text-[11px] font-medium text-muted-foreground">
                        {selectedTags.length} tag{selectedTags.length > 1 ? "s" : ""} attached
                      </span>
                    )}
                  </div>

                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 p-2 bg-muted/20 border border-border rounded-lg">
                      {selectedTags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-md text-[11px] font-medium"
                        >
                          #{tag}
                          <button
                            type="button"
                            onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                            className="hover:bg-primary/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const clean = tagInput.trim().toLowerCase().replace(/^#/, "");
                          if (clean && !selectedTags.includes(clean)) {
                            setSelectedTags(prev => [...prev, clean]);
                            if (!existingTags.includes(clean)) setExistingTags(prev => [...prev, clean].sort());
                          }
                          setTagInput("");
                        }
                      }}
                      placeholder="Type tag & press Enter or comma..."
                      className="rounded-lg h-8.5 text-xs"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const clean = tagInput.trim().toLowerCase().replace(/^#/, "");
                        if (clean && !selectedTags.includes(clean)) {
                          setSelectedTags(prev => [...prev, clean]);
                          if (!existingTags.includes(clean)) setExistingTags(prev => [...prev, clean].sort());
                        }
                        setTagInput("");
                      }}
                      className="rounded-lg h-8.5 px-3 text-xs"
                    >
                      Add Tag
                    </Button>
                  </div>

                  {existingTags.length > 0 && (
                    <div className="pt-1">
                      <p className="text-[11px] text-muted-foreground mb-1 font-medium">Quick Select Existing Tags:</p>
                      <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                        {existingTags.map(tag => {
                          const isSelected = selectedTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedTags(selectedTags.filter(t => t !== tag));
                                } else {
                                  setSelectedTags([...selectedTags, tag]);
                                }
                              }}
                              className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-all border ${
                                isSelected
                                  ? "bg-primary text-white border-primary"
                                  : "bg-background hover:bg-muted text-muted-foreground border-border"
                              }`}
                            >
                              #{tag} {isSelected && "✓"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Billing Codes & Items */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="space-y-3"
            >
              <div className="bg-card border border-border rounded-xl p-4 shadow-2xs space-y-3">
                <div>
                  <h2 className="text-base font-bold flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-primary" /> CPT / Billing Code Preparation
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Select billing categories, procedure codes, adjust quantities and line-item discounts, or add custom supply items.
                  </p>
                </div>

                {/* CPT Category & Sub-Category Selection Moved to Step 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl bg-muted/20 border border-border">
                  <div>
                    <Label className="text-xs font-semibold text-foreground flex items-center justify-between">
                      <span>CPT Code Category *</span>
                      <span className="text-[10px] text-muted-foreground font-normal">Billing Category</span>
                    </Label>
                    <Select
                      value={form.category}
                      onValueChange={val => {
                        setForm(prev => ({ ...prev, category: val, categoryType: "All Subcategories" }));
                      }}
                    >
                      <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background border-border hover:border-primary/50 transition-colors">
                        <div className="flex items-center gap-2 truncate">
                          <Layers className="w-3.5 h-3.5 text-primary shrink-0" />
                          <SelectValue placeholder="Select CPT Category" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl text-xs max-h-60">
                        {masterCptCategoryOptions.map((cat: string) => (
                          <SelectItem key={cat} value={cat} className="text-xs py-1.5 cursor-pointer">
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-foreground flex items-center justify-between">
                      <span>CPT Code Subcategory *</span>
                      <span className="text-[10px] text-muted-foreground font-normal">Sub-Specialty</span>
                    </Label>
                    <Select
                      value={form.categoryType}
                      onValueChange={val => setForm(prev => ({ ...prev, categoryType: val }))}
                    >
                      <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background border-border hover:border-primary/50 transition-colors">
                        <div className="flex items-center gap-2 truncate">
                          <GitBranch className="w-3.5 h-3.5 text-primary shrink-0" />
                          <SelectValue placeholder="Select CPT Subcategory" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl text-xs max-h-60">
                        {masterCptSubcategoryOptions.map((st: string) => (
                          <SelectItem key={st} value={st} className="text-xs py-1.5 cursor-pointer">
                            {st}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-muted-foreground" />
                  <Input
                    value={cptSearch}
                    onChange={e => setCptSearch(e.target.value)}
                    placeholder="Search CPT code or description..."
                    className="pl-9 rounded-lg h-8.5 text-xs"
                  />
                </div>

                {/* Selected Codes List */}
                {selectedCpts.length > 0 && (
                  <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-bold text-xs text-primary flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Selected CPT Codes ({selectedCpts.length})
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCpts([])}
                        className="text-[11px] text-rose-500 hover:text-rose-600 hover:bg-rose-50 rounded-md h-6 px-2"
                      >
                        Clear All
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {selectedCpts.map(id => {
                        const code = allAvailableCptCodes.find((c: any) => c.id === id || c.originalMasterId === id || c.providerOverrideId === id);
                        if (!code) return null;
                        const unit = cptUnits[id] || 1;
                        const basePrice = Number(code.price || 0);
                        const adj = cptAdjustments[id] || { type: "percent", value: 0 };
                        
                        const currentPercent = adj.percent !== undefined
                          ? adj.percent
                          : adj.type === "percent"
                          ? (adj.value || 0)
                          : basePrice > 0 && (adj.fixed !== undefined || (adj.type === "fixed" && adj.value))
                          ? Math.round((((adj.fixed ?? adj.value) / basePrice) * 100) * 100) / 100
                          : 0;

                        const currentDiscAmt = adj.fixed !== undefined
                          ? adj.fixed
                          : adj.type === "fixed"
                          ? (adj.value || 0)
                          : basePrice > 0 && (adj.percent !== undefined || (adj.type === "percent" && adj.value))
                          ? Math.round((basePrice * ((adj.percent ?? adj.value) / 100)) * 100) / 100
                          : 0;

                        const finalAmount = adj.adjustedPrice !== undefined
                          ? adj.adjustedPrice
                          : Math.max(0, Math.round((basePrice - currentDiscAmt) * 100) / 100);

                        return (
                          <div
                            key={id}
                            className="p-3 bg-background border border-border rounded-xl space-y-2 text-xs"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-primary/15 text-primary border-0 font-mono text-[11px] px-1.5 py-0 shrink-0">
                                    {code.code}
                                  </Badge>
                                  <span className="font-semibold text-xs truncate">{code.description}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  Base Rate: ${basePrice.toFixed(2)} | Total: ${(finalAmount * unit).toFixed(2)}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                {/* Quantity Counter */}
                                <div className="flex items-center gap-1 bg-muted p-0.5 rounded-md">
                                  <button
                                    type="button"
                                    onClick={() => setCptUnits(prev => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) - 1) }))}
                                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-background text-xs font-bold"
                                  >
                                    -
                                  </button>
                                  <span className="w-6 text-center text-xs font-semibold">{unit}</span>
                                  <button
                                    type="button"
                                    onClick={() => setCptUnits(prev => ({ ...prev, [id]: (prev[id] || 1) + 1 }))}
                                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-background text-xs font-bold"
                                  >
                                    +
                                  </button>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => toggleCpt(id)}
                                  className="text-muted-foreground hover:text-rose-500 p-1"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Adjustable Controls: Disc % / Disc Amt ($) / Final Amount ($) */}
                            <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border/60 text-xs">
                              <div className="flex items-center gap-2.5 flex-wrap">
                                {/* Field 1: Disc % */}
                                <div className="flex items-center gap-1 bg-muted/60 px-2 py-1 rounded-lg border border-border">
                                  <span className="text-[10px] font-bold text-muted-foreground uppercase">Disc %:</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    placeholder="0"
                                    value={currentPercent === 0 && !adj.percent ? "" : currentPercent}
                                    onChange={(e) => {
                                      const valStr = e.target.value;
                                      if (valStr === "") {
                                        setCptAdjustments(prev => ({
                                          ...prev,
                                          [id]: { type: "percent", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                        }));
                                        return;
                                      }
                                      const p = Math.max(0, Math.min(100, parseFloat(valStr) || 0));
                                      const discAmt = Math.round((basePrice * (p / 100)) * 100) / 100;
                                      const finalPrice = Math.max(0, Math.round((basePrice - discAmt) * 100) / 100);
                                      setCptAdjustments(prev => ({
                                        ...prev,
                                        [id]: {
                                          type: "percent",
                                          value: p,
                                          percent: p,
                                          fixed: discAmt,
                                          adjustedPrice: finalPrice
                                        }
                                      }));
                                    }}
                                    className="w-14 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-foreground"
                                  />
                                  <span className="text-[11px] font-semibold text-muted-foreground">%</span>
                                </div>

                                {/* Field 2: Disc Amt ($) */}
                                <div className="flex items-center gap-1 bg-muted/60 px-2 py-1 rounded-lg border border-border">
                                  <span className="text-[10px] font-bold text-muted-foreground uppercase">Disc Amt ($):</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max={basePrice}
                                    step="0.01"
                                    placeholder="0.00"
                                    value={currentDiscAmt === 0 && !adj.fixed ? "" : currentDiscAmt}
                                    onChange={(e) => {
                                      const valStr = e.target.value;
                                      if (valStr === "") {
                                        setCptAdjustments(prev => ({
                                          ...prev,
                                          [id]: { type: "fixed", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                        }));
                                        return;
                                      }
                                      const discAmt = Math.max(0, Math.min(basePrice, parseFloat(valStr) || 0));
                                      const p = basePrice > 0 ? Math.round(((discAmt / basePrice) * 100) * 100) / 100 : 0;
                                      const finalPrice = Math.max(0, Math.round((basePrice - discAmt) * 100) / 100);
                                      setCptAdjustments(prev => ({
                                        ...prev,
                                        [id]: {
                                          type: "fixed",
                                          value: discAmt,
                                          percent: p,
                                          fixed: discAmt,
                                          adjustedPrice: finalPrice
                                        }
                                      }));
                                    }}
                                    className="w-16 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-foreground"
                                  />
                                  <span className="text-[10px] font-medium text-muted-foreground">off</span>
                                </div>

                                {/* Field 3: Final Amount ($) */}
                                <div className="flex items-center gap-1 bg-primary/10 px-2 py-1 rounded-lg border border-primary/20">
                                  <span className="text-[10px] font-bold text-primary uppercase">Final Amount ($):</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder={basePrice.toFixed(2)}
                                    value={finalAmount}
                                    onChange={(e) => {
                                      const valStr = e.target.value;
                                      if (valStr === "") {
                                        setCptAdjustments(prev => ({
                                          ...prev,
                                          [id]: { type: "fixed", value: 0, percent: 0, fixed: 0, adjustedPrice: basePrice }
                                        }));
                                        return;
                                      }
                                      const finalPrice = Math.max(0, parseFloat(valStr) || 0);
                                      const discAmt = Math.max(0, Math.round((basePrice - finalPrice) * 100) / 100);
                                      const p = basePrice > 0 ? Math.round(((discAmt / basePrice) * 100) * 100) / 100 : 0;
                                      setCptAdjustments(prev => ({
                                        ...prev,
                                        [id]: {
                                          type: "fixed",
                                          value: discAmt,
                                          percent: p,
                                          fixed: discAmt,
                                          adjustedPrice: finalPrice
                                        }
                                      }));
                                    }}
                                    className="w-16 h-6 text-center text-xs font-bold bg-transparent focus:outline-none text-primary"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available CPT Codes List */}
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Available Billing Codes
                  </Label>
                  <div className="max-h-44 overflow-y-auto border border-border rounded-lg divide-y divide-border bg-background">
                    {filteredCptCodes.slice(0, 100).map((c: any) => {
                      const isSelected = selectedCpts.includes(c.id);
                      return (
                        <div
                          key={c.id}
                          onClick={() => toggleCpt(c.id)}
                          className={`p-2 flex items-center justify-between cursor-pointer transition-colors ${
                            isSelected ? "bg-primary/10" : "hover:bg-muted/50"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Checkbox checked={isSelected} />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-xs text-primary">{c.code}</span>
                                <span className="text-[11px] text-muted-foreground">({c.category || "Medical"})</span>
                              </div>
                              <p className="text-[11px] text-foreground mt-0.5 line-clamp-1">{c.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-xs text-foreground">${c.price || 100}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Custom Items Section */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs font-semibold flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5 text-primary" /> Custom Non-CPT Items / Add-ons
                  </Label>

                  {customItems.length > 0 && (
                    <div className="space-y-1">
                      {customItems.map(item => (
                        <div
                          key={item.id}
                          className="p-2 bg-muted/30 border border-border rounded-lg flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-semibold">{item.name}</span>
                            {item.description && <span className="text-muted-foreground ml-2">— {item.description}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-xs">
                              ${item.price} x {item.unit || 1} = ${(Number(item.price) * (Number(item.unit) || 1)).toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => setCustomItems(customItems.filter(i => i.id !== item.id))}
                              className="text-rose-500 hover:text-rose-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <Input
                      placeholder="Item Name"
                      value={customName}
                      onChange={e => setCustomName(e.target.value)}
                      className="rounded-lg text-xs h-8.5"
                    />
                    <Input
                      placeholder="Unit Price ($)"
                      type="number"
                      value={customPrice}
                      onChange={e => setCustomPrice(e.target.value)}
                      className="rounded-lg text-xs h-8.5"
                    />
                    <Input
                      placeholder="Quantity"
                      type="number"
                      value={customUnit}
                      onChange={e => setCustomUnit(e.target.value)}
                      className="rounded-lg text-xs h-8.5"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!customName || !customPrice) return;
                        setCustomItems([
                          ...customItems,
                          {
                            id: Math.random().toString(),
                            name: customName,
                            description: customDesc,
                            price: Number(customPrice),
                            unit: Number(customUnit) || 1
                          }
                        ]);
                        setCustomName("");
                        setCustomDesc("");
                        setCustomPrice("");
                        setCustomUnit("1");
                      }}
                      className="rounded-lg text-xs h-8.5"
                    >
                      Add Item
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Review & Pricing */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="space-y-3"
            >
              <div className="bg-card border border-border rounded-xl p-4 shadow-2xs space-y-3">
                <div>
                  <h2 className="text-base font-bold flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" /> Pricing Summary & Review
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Review bundle items, calculated retail cost, applied bundle discount, and publish status.
                  </p>
                </div>

                {/* Summary Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3.5 rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20">
                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                      Total Retail Cost
                    </div>
                    <div className="text-xl font-extrabold text-foreground mt-0.5">
                      ${calculatedTotal.toFixed(2)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Sum of standard rates
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                      Bundle Discount
                    </div>
                    <div className="text-xl font-extrabold text-primary mt-0.5">
                      {form.discountPercent}% Off
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Save ${(calculatedTotal - finalPriceValue).toFixed(2)}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                      Final Bundle Price
                    </div>
                    <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">
                      ${finalPriceValue.toFixed(2)}
                    </div>
                    <div className="text-[11px] text-emerald-600/80 font-medium mt-0.5">
                      Patient Price
                    </div>
                  </div>
                </div>

                {/* Pricing Input Overrides */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Bundle Discount Percentage (%)</Label>
                    <Input
                      type="number"
                      value={form.discountPercent}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(prev => ({
                          ...prev,
                          discountPercent: val,
                          finalPrice: calculatedTotal ? (calculatedTotal * (1 - (Number(val) || 0) / 100)).toFixed(2) : ""
                        }));
                      }}
                      className="mt-1 rounded-lg h-9 text-xs"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Override Final Package Price ($)</Label>
                    <Input
                      type="number"
                      value={form.finalPrice}
                      onChange={e => {
                        const val = e.target.value;
                        const numVal = Number(val);
                        const disc = calculatedTotal ? (((calculatedTotal - numVal) / calculatedTotal) * 100).toFixed(2) : "0";
                        setForm(prev => ({
                          ...prev,
                          finalPrice: val,
                          discountPercent: disc
                        }));
                      }}
                      placeholder={`Calculated: $${finalPriceValue.toFixed(2)}`}
                      className="mt-1 rounded-lg h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Payment Structure & Recurring Billing */}
                <div className="space-y-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs font-bold text-foreground flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-primary" /> Payment Model & Subscription Structure
                      </Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Configure whether this service package is billed as a single one-time charge or converted into a recurring subscription.
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label htmlFor="subscription-toggle" className="text-xs font-semibold cursor-pointer text-foreground flex items-center gap-1.5">
                          Enable Recurring Payment Schedule / Subscription
                          <Badge variant="outline" className="text-[10px] font-bold text-primary border-primary/30 bg-primary/5">
                            Recurring Plan
                          </Badge>
                        </label>
                        <p className="text-[11px] text-muted-foreground">
                          Allows members to subscribe to this service bundle with scheduled installment payments instead of full upfront payment.
                        </p>
                      </div>
                      <Switch
                        id="subscription-toggle"
                        checked={isSubscription}
                        onCheckedChange={checked => {
                          setIsSubscription(checked);
                          if (checked && !recurringAmount && finalPriceValue > 0) {
                            setRecurringAmount((finalPriceValue / 12).toFixed(2));
                          }
                        }}
                      />
                    </div>

                    {isSubscription && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3 pt-2 border-t border-border/60"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Billing Frequency */}
                          <div>
                            <Label className="text-xs font-semibold">Billing Schedule / Interval</Label>
                            <Select
                              value={subscriptionInterval}
                              onValueChange={val => {
                                setSubscriptionInterval(val);
                                if (finalPriceValue > 0) {
                                  let divisor = 12;
                                  if (val === "QUARTERLY") divisor = 4;
                                  if (val === "BI_WEEKLY") divisor = 26;
                                  if (val === "WEEKLY") divisor = 52;
                                  if (val === "YEARLY") divisor = 1;
                                  const suggested = Math.max(1, Math.round((finalPriceValue / divisor) * 100) / 100);
                                  setRecurringAmount(suggested.toFixed(2));
                                }
                              }}
                            >
                              <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background">
                                <SelectValue placeholder="Select Frequency" />
                              </SelectTrigger>
                              <SelectContent className="rounded-xl text-xs">
                                <SelectItem value="MONTHLY">Monthly Billing</SelectItem>
                                <SelectItem value="QUARTERLY">Quarterly Billing</SelectItem>
                                <SelectItem value="BI_WEEKLY">Bi-Weekly Billing</SelectItem>
                                <SelectItem value="WEEKLY">Weekly Billing</SelectItem>
                                <SelectItem value="YEARLY">Annual Billing</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Recurring Amount */}
                          <div>
                            <Label className="text-xs font-semibold flex items-center justify-between">
                              <span>Recurring Installment ($)</span>
                              {finalPriceValue > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    let divisor = 12;
                                    if (subscriptionInterval === "QUARTERLY") divisor = 4;
                                    if (subscriptionInterval === "BI_WEEKLY") divisor = 26;
                                    if (subscriptionInterval === "WEEKLY") divisor = 52;
                                    if (subscriptionInterval === "YEARLY") divisor = 1;
                                    const cyclesNum = billingCycles ? parseInt(billingCycles, 10) : divisor;
                                    const calc = Math.max(1, Math.round((finalPriceValue / (cyclesNum || 1)) * 100) / 100);
                                    setRecurringAmount(calc.toFixed(2));
                                  }}
                                  className="text-[10px] text-primary hover:underline font-normal"
                                >
                                  Auto-calculate
                                </button>
                              )}
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={recurringAmount}
                              onChange={e => setRecurringAmount(e.target.value)}
                              className="mt-1 rounded-lg h-9 text-xs bg-background font-bold text-foreground"
                            />
                          </div>

                          {/* Billing Cycles / Duration */}
                          <div>
                            <Label className="text-xs font-semibold">Total Billing Cycles (Optional)</Label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="e.g. 12 (Blank = Continuous)"
                              value={billingCycles}
                              onChange={e => setBillingCycles(e.target.value)}
                              className="mt-1 rounded-lg h-9 text-xs bg-background"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Leave blank for ongoing recurring subscription.
                            </p>
                          </div>
                        </div>

                        {/* Subscription Summary Badge / Banner */}
                        <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20 text-xs flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary shrink-0" />
                            <div>
                              <span className="font-bold text-foreground">Configured Schedule: </span>
                              <span className="font-semibold text-primary">
                                ${parseFloat(recurringAmount || "0").toFixed(2)} / {subscriptionInterval.toLowerCase().replace('_', '-')}
                              </span>
                              {billingCycles ? ` (${billingCycles} total billing cycles)` : " (Continuous Subscription)"}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Featured Bundle Toggle */}
                    <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 space-y-2 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label htmlFor="featured-toggle" className="text-xs font-bold text-foreground cursor-pointer flex items-center gap-1.5">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-400" /> Market as "Featured Service"
                            <Badge variant="outline" className="text-[10px] font-bold text-amber-600 dark:text-amber-400 border-amber-500/30 bg-amber-500/10">
                              ⭐ Featured Common Service
                            </Badge>
                          </label>
                          <p className="text-[11px] text-muted-foreground">
                            Showcase this bundle prominently with a star badge across public provider profile pages and search results to highlight top service offerings.
                          </p>
                        </div>
                        <Switch
                          id="featured-toggle"
                          checked={isFeatured}
                          onCheckedChange={checked => setIsFeatured(checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bundle Status */}
                <div>
                  <Label className="text-xs font-semibold">Bundle Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={val => setForm(prev => ({ ...prev, status: val }))}
                  >
                    <SelectTrigger className="mt-1 rounded-lg h-9 text-xs bg-background">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl text-xs">
                      <SelectItem value="active">Active (Visible to Patients & Members)</SelectItem>
                      <SelectItem value="inactive">Inactive / Draft (Hidden)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Disclaimer Checkbox */}
                <div className="p-3 rounded-xl border border-border bg-muted/20 space-y-2">
                  <div className="flex items-start gap-2.5">
                    <Checkbox
                      id="disclaimer"
                      checked={disclaimerAccepted}
                      onCheckedChange={checked => setDisclaimerAccepted(!!checked)}
                      className="mt-0.5"
                    />
                    <label htmlFor="disclaimer" className="text-[11px] text-muted-foreground leading-relaxed cursor-pointer">
                      <span className="font-semibold text-foreground">Good Faith Estimate Disclaimer:</span> I confirm that all
                      procedure codes, estimated prices, and bundled services included above are compliant with billing regulations
                      and represent fair cash prices offered at our facility.
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ProviderLayout>
  );
}
